import api from 'client/api'
import { defineStore } from 'pinia'
import { translate as t } from '../i18n/i18n'
import { rootState } from './hydrate.js'
import { getUserEmail } from 'Shared/userUtils'
import cloneDeep from 'lodash/cloneDeep'
import { useWaitStore } from './wait'

export const t_offset = (new Date()).getTimezoneOffset()
export const CONTRAST_SCHEME = {
  dark: 1,
  light: 0
}
export const DEFAULT_CONTRAST = CONTRAST_SCHEME.dark

const detectBrowserColorScheme = () => {
  const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches
  const isLightMode = window.matchMedia('(prefers-color-scheme: light)').matches

  if (isDarkMode) {
    return CONTRAST_SCHEME.dark
  } else if (isLightMode) {
    return CONTRAST_SCHEME.light
  }

  return DEFAULT_CONTRAST
}

export const useUserStore = defineStore('user', {
  state: () => ({
    email: null,
    name: null,
    initials: null,
    picture: null,
    ibm_tp: null,
    provider: null,
    originalProvider: null,
    ibm_session_id: null,
    prefs: {},
    permissions: {},
    groups: [], // only needed for federated search
    ...(rootState.__user ?? {})
  }),
  getters: {
    useUTC(state) {
      return !!state.prefs?.showutc ?? false
    },
    timezoneOffset(state) {
      if (!state.prefs) {
        return 0
      }
      return state.prefs.showutc ? 0 : t_offset
    },
    contrast(state) {
      if (!state.prefs) {
        return DEFAULT_CONTRAST
      }

      const defaultContrast = detectBrowserColorScheme()
      return state.prefs.contrast !== undefined ? state.prefs.contrast : defaultContrast
    },
    userPermissions(state) {
      return state.permissions || {}
    },
    userRole(state) {
      const { isGM, isOwner, isAdmin, isReadOnly } = state.permissions || {}

      if (isGM) {
        return t('switchOrganization.gm')
      } else if (isOwner) {
        return t('switchOrganization.owner')
      } else if (isAdmin) {
        return t('switchOrganization.admin')
      } else if (isReadOnly) {
        return t('switchOrganization.readonly')
      }

      return t('switchOrganization.member')
    },
    // currentUserEmail getter is NOT to be used as a unique identifier.
    // This is used only for display and transactional email services
    currentUserEmail(state) {
      return getUserEmail(state)
    },
    isDemoUser(state) {
      return state.currentUserEmail?.endsWith('trymezmo.com') ?? false
    }
  },

  actions: {
    updateUserPrefs(prefs) {
      if (!this.prefs) {
        this.prefs = {}
      }
      for (const [prop, value] of Object.entries(prefs)) {
        this.prefs[prop] = value
      }
    },
    async setAndPersistUserPrefs(prefs) {
      if (typeof prefs !== 'object') {
        return
      }
      const getPrefsSnapshot = (fields) => {
        const snapshot = {}
        for (const field of fields) {
          snapshot[field] = cloneDeep(this.prefs[field])
        }
        return snapshot
      }
      return new Promise((resolve, reject) => {
        const snapshot = getPrefsSnapshot(Object.keys(prefs))
        this.updateUserPrefs(prefs)
        const waitStore = useWaitStore()
        waitStore.start('saving user prefs')
        api.account.prefs.update({ prefs })
          .then((response) => {
            if (response) {
              resolve(response.data)
            } else {
              throw Error('Bad response from server.')
            }
          })
          .catch((err) => {
            this.updateUserPrefs(snapshot) // roll back
            reject(err)
          })
          .finally(() => {
            waitStore.end('saving user prefs')
          })
      })
    }
  }
})
